import {
    Button, Card, Col, ConfigProvider, DatePicker, Empty, Form, Input, Modal, Row,
    Select, Space, Spin, Table, Tag, Tooltip, Typography
} from "antd";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { createRef, useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// Gabarit PDF partagé des états PoolIns (en-tête/pied, logos, pagination).
import {
    NAVY, drawEtatHeaderFooter, finalizeEtatPdf, loadEntete, loadLogos, loadLogoCompagnieConnectee,
} from "../stats/sinistre/etatsSinistresFunctions";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import {
    PrinterOutlined, ReloadOutlined, CreditCardOutlined, ExclamationCircleFilled,
    FileDoneOutlined, BankOutlined, CheckCircleTwoTone, EditOutlined, StopOutlined,
    FilePdfOutlined, CalendarOutlined
} from "@ant-design/icons";
import { rightsService } from "../../services/rightsService";

import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";
import { frenchDate } from "../../variables/functions";
import { displayNumbers } from "../../variables/numbersFunctions";
import { StatCard } from "../../components/statistics/StatCard";
import { COLORS } from "../../components/ui/colors";
import BorderauReversementCompagnie from "./docs/BorderauReversementCompagnie";
import EtatBordereauxReversement from "./docs/EtatBordereauxReversement";
import { getCompagnieLogo } from "../../hooks/useFilesHooks";

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

const MODE_COLORS = {
    ESPECES: "blue", RISTOURNE: "green", BANQUE: "gold",
    VIREMENT: "geekblue", MOBILE_MONEY: "magenta", COMPENSATION: "purple",
};

const isVerse = (b) => !!(b?.modePaiement && b.modePaiement !== "-");

// Bénéficiaire / reverseur : agence rattachée si résolue, sinon REPLI sur l'observation
// (« Bénéficiaire : NOM (TYPE - CODE) » figé à la génération) — couvre agence nulle/historisée.
const beneficiaireNomOf = (b) =>
    // DTO plat (liste paginée) ; repli sur l'objet imbriqué (détail chargé à la demande),
    // puis sur l'observation (« Bénéficiaire : NOM (TYPE) » figé à la génération) qui couvre
    // les agences nulles ou historisées.
    b?.agenceNom
    || b?.agence?.nom
    || (/Bénéficiaire\s*:\s*(.+?)\s*\(/.exec(b?.observation || "")?.[1]?.trim())
    || null;

// « Caissier » d'un bordereau = son VALIDATEUR : c'est l'utilisateur connecté au moment du
// versement en compagnie (BordereauService pose setValidateur(user) à ce moment-là). Le
// producteur, lui, n'est que l'éditeur du bordereau.
const caissierNomOf = (b) => {
    if (b?.validateurNom) return b.validateurNom;   // DTO plat (liste paginée)
    const p = b?.validateur?.personne;              // repli : détail complet
    if (!p) return null;
    const nom = `${p.nom || ""} ${p.prenom || ""}`.trim();
    return nom || b?.validateur?.username || null;
};

// Ne renvoie une valeur que si elle est affichable telle quelle (chaîne/nombre) : protège les
// rendus {r.x} contre un objet, qui ferait planter React (error #31 → page blanche).
const texteOuNull = (v) => (typeof v === "string" || typeof v === "number") ? v : null;

// Mappe les règlements d'un bordereau persisté vers le format « plat » attendu par
// BorderauReversementCompagnie (les champs sont nichés sous reglement.contrat).
const mapBordereauToEmissions = (bord) => (bord?.reglements || []).map((reg) => ({
    codeId: reg.codeId,
    numeroPolice: reg.contrat?.numeroPoliceP || reg.contrat?.numeroPolice,
    // Le détail est désormais chargé à la demande via /api/bordereau/{id}, qui renvoie
    // l'ENTITÉ : `avenant` y est un OBJET Avenant, alors que le DTO exposait `avenantCode`
    // (chaîne). Le document fait {r.avenant} → rendre un objet plante React (« error #31 :
    // objects are not valid as a React child », page blanche). On normalise donc en chaîne,
    // quelle que soit la forme reçue.
    avenant: texteOuNull(reg.avenantCode) || texteOuNull(reg.avenant) || texteOuNull(reg.avenant?.code),
    nomSouscripteur: reg.contrat?.souscripteur?.nom,
    prenomSouscripteur: reg.contrat?.souscripteur?.prenom,
    dateEffet: reg.contrat?.dateEffet,
    dateEcheance: reg.contrat?.dateEcheance,
    // Catégorie : le DTO l'exposait à plat (code_cat, via sous-requête PG). L'ENTITÉ ne porte
    // PAS de catégorie sur le contrat — elle vit sur l'engin (contrat.engins[].categorie), et
    // `Categorie.codeCategorie` est mappé sur la MÊME colonne code_cat → valeur identique.
    // On tente donc, dans l'ordre : DTO plat → engin de l'entité → replis.
    categorie: texteOuNull(reg.contrat?.categorie)
        || texteOuNull(reg.contrat?.categorie?.codeCategorie)
        || texteOuNull(reg.contrat?.engins?.[0]?.categorie?.codeCategorie)
        || texteOuNull(reg.contrat?.engins?.[0]?.categorie?.nom)
        || texteOuNull(reg.contrat?.categorie?.nom),
    montant: reg.montant,
    accessoires: reg.accessoires,
    taxes: reg.taxes,
    tauxCommission: reg.tauxCommission,
    commission: reg.commissions != null ? reg.commissions : reg.commission,
    dateReglement: reg.dateReglement,
}));

/** Aperçu imprimable d'un bordereau de reversement (réutilise le document refondu). */
const PrintableBordereau = ({ bordereau }) => {
    const ref = createRef();
    // Journalisation de l'IMPRESSION PAPIER (le bordereau est une pièce comptable remise à la
    // compagnie). Déclenchée au lancement réel de l'impression, PAS à l'ouverture de l'aperçu :
    // consulter un bordereau ne laisse donc aucune trace.
    // NB : les navigateurs n'exposent pas l'issue de la boîte d'impression (imprimé vs annulé) —
    // on trace donc le LANCEMENT, ce qui est l'information disponible la plus fidèle.
    const handlePrint = useReactToPrint({
        content: () => ref.current,
        onBeforePrint: () => {
            if (!bordereau?.codeId) return;
            axiosInstance.post(`/api/bordereau/${bordereau.codeId}/print-log`)
                // Échec silencieux : une trace manquante ne doit jamais empêcher d'imprimer.
                .catch((e) => console.log("print-log:", e));
        },
    });
    const emissions = mapBordereauToEmissions(bordereau);

    // Logo de la compagnie destinataire du reversement (affiché à côté de l'en-tête du Pool).
    const [logoCompagnie, setLogoCompagnie] = useState(null);
    useEffect(() => {
        const code = organismeCodeOf(bordereau);
        if (!code) return undefined;
        let url;
        getCompagnieLogo(code)
            .then(async (response) => {
                const bytes = await response.arrayBuffer();
                const blob = new Blob([bytes], { type: "image/png" });
                url = URL.createObjectURL(blob);
                setLogoCompagnie(url);
            })
            .catch((err) => console.log(err));
        return () => { if (url) URL.revokeObjectURL(url); };
    }, [bordereau]);

    return (
        <Card style={{ borderRadius: 14, marginBottom: 8, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
            bodyStyle={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <Space wrap>
                    <Tag color="cyan" style={{ borderRadius: 6 }}><BankOutlined /> {beneficiaireNomOf(bordereau) || "—"}</Tag>
                    <Tag color="orange" style={{ borderRadius: 6 }}>N° {bordereau?.numeroBordereau || "—"}</Tag>
                    <Text type="secondary">· {emissions.length} règlement(s)</Text>
                    {isVerse(bordereau)
                        ? <Tag color="green" style={{ borderRadius: 6 }}>Versé</Tag>
                        : <Tag style={{ borderRadius: 6 }}>Non versé</Tag>}
                </Space>
                <Button icon={<PrinterOutlined />} onClick={handlePrint} type="primary"
                    style={{ background: COLORS.gradient, border: "none", borderRadius: 8 }}>
                    Imprimer
                </Button>
            </div>
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 8, overflowX: "auto" }}>
                <BorderauReversementCompagnie
                    ref={ref}
                    emissions={emissions}
                    logoCompagnie={logoCompagnie}
                    numeroBordereau={bordereau?.numeroBordereau}
                    reverseur={beneficiaireNomOf(bordereau)}
                    periodeDebut={bordereau?.periodeDebut}
                    periodeFin={bordereau?.periodeFin}
                    paiement={isVerse(bordereau) ? {
                        modePaiement: bordereau?.modePaiement,
                        numeroPaiement: bordereau?.numeroPaiement,
                        banque: bordereau?.banque,
                        dateReglement: bordereau?.dateReglement,
                        commentaireEncaissement: bordereau?.commentaireEncaissement,
                    } : null}
                />
            </div>
        </Card>
    );
};

const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";

// Compagnie (organisme) d'un bordereau : déduite des règlements (contrat.organismeCode).
const organismeCodeOf = (b) => {
    // Le DTO plat porte directement organismeCode (colonne m_bordereau.organisme_code) : plus
    // besoin de parcourir les règlements — c'est précisément ce parcours qui obligeait à
    // transporter tout le détail dans la liste.
    if (b?.organismeCode) return b.organismeCode;
    const r = (b?.reglements || []).find((x) => x?.contrat?.organismeCode);
    return r?.contrat?.organismeCode || null;
};

const BorderauReversement = () => {
    const [allDatas, setAllDatas] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [search, setSearch] = useState("");
    const [statutFilter, setStatutFilter] = useState("TOUS");
    // Filtre par CAISSIER (validateur du versement). null = tous.
    const [caissierFilter, setCaissierFilter] = useState(null);
    // Pagination SERVEUR : la liste ne charge plus tous les bordereaux (le DTO allégé + /page
    // remplacent l'ancien /api/bordereau qui embarquait règlements et contrats).
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    // Cartes : agrégées côté serveur sur l'ENSEMBLE filtré (une page fausserait les KPI).
    const [statsServer, setStatsServer] = useState({ total: 0, montantTotal: 0, nbVerses: 0, nbNonVerses: 0 });
    // Options des filtres : sur tout le périmètre, pas seulement la page affichée.
    const [optionsServer, setOptionsServer] = useState({ apporteurs: [], caissiers: [] });
    // Map code organisme -> raison sociale (pour le tag compagnie, vue Pool)
    const [compagnies, setCompagnies] = useState({});

    const [banques, setBanques] = useState([]);
    const [bordereauToPay, setBordereauToPay] = useState(null);

    const [openModalBorderau, setOpenModalBorderau] = useState(false);
    const [modalModePaiement, setModalModePaiement] = useState(false);
    // Annulation d'un bordereau non versé — réservée à l'habilitation HLA101.
    const peutAnnuler = rightsService("HLA101");
    const [modalAnnulation, setModalAnnulation] = useState(false);
    const [bordereauAAnnuler, setBordereauAAnnuler] = useState(null);
    const [motifAnnulation, setMotifAnnulation] = useState("");
    const [annulationEnCours, setAnnulationEnCours] = useState(false);
    // Modification EXCEPTIONNELLE de la période affichée (même habilitation que l'annulation : HLA101).
    const [modalPeriode, setModalPeriode] = useState(false);
    const [bordereauPeriode, setBordereauPeriode] = useState(null);
    const [periodeEdit, setPeriodeEdit] = useState([null, null]);
    const [periodeEnCours, setPeriodeEnCours] = useState(false);
    // true = correction du mode de règlement d'un bordereau déjà versé ;
    // false = encaissement initial.
    const [editMode, setEditMode] = useState(false);
    const [modePaiement, setModePaiement] = useState("");
    const [numeroPaiement, setNumeroPaiement] = useState("P");
    const [banque, setBanque] = useState("BANQUE");
    // Date de valeur du virement (mode « Banque (Virement) ») — persistée en date de règlement.
    const [dateVirement, setDateVirement] = useState(null);
    const [commentaireEncaissement, setCommentaireEncaissement] = useState("");
    const [modePaiementForm] = Form.useForm();

    // ── État des reversements versés (filtre période + agence/apporteur optionnel) ──
    const [etatPeriode, setEtatPeriode] = useState([dayjs().startOf("month"), dayjs()]);
    // Période de chargement de la LISTE (création des bordereaux) — borne le volume chargé
    // (détail des règlements imbriqué → payload lourd). Défaut : année en cours.
    const [periode, setPeriode] = useState([dayjs().startOf("year"), dayjs()]);
    const [etatAgence, setEtatAgence] = useState(null);
    const [etatData, setEtatData] = useState([]);
    const [etatLogos, setEtatLogos] = useState({});
    const [etatLoading, setEtatLoading] = useState(false);
    const [showEtatModal, setShowEtatModal] = useState(false);
    const etatPrintRef = useRef();
    const handlePrintEtat = useReactToPrint({ content: () => etatPrintRef.current });

    // Paramètres de filtrage communs à la liste, aux stats et aux exports : une seule
    // définition → le tableau, les cartes et l'état journalier ne peuvent pas diverger.
    const filtresParams = () => {
        const p = {};
        if (periode && periode[0] && periode[1]) {
            p.startDate = periode[0].format("YYYY-MM-DD");
            p.endDate = periode[1].format("YYYY-MM-DD");
        }
        if (search.trim()) p.search = search.trim();
        if (statutFilter && statutFilter !== "TOUS") p.statut = statutFilter;
        if (caissierFilter) p.caissier = caissierFilter;
        if (etatAgence) p.apporteur = etatAgence;
        return p;
    };

    const fetchData = () => {
        setLoadingData(true);
        const params = { ...filtresParams(), page: page - 1, size: pageSize };
        axiosInstance.get(`/api/bordereau/page`, { params })
            .then((res) => {
                setAllDatas(res.data?.content || []);
                setTotal(res.data?.totalElements || 0);
                setLoadingData(false);
            })
            .catch(() => setLoadingData(false));

        axiosInstance.get(`/api/bordereau/page/stats`, { params: filtresParams() })
            .then((res) => setStatsServer(res.data || {}))
            .catch(() => { /* les cartes restent sur la dernière valeur connue */ });
    };

    /** Toutes les lignes du périmètre filtré (état journalier / exports) — hors pagination. */
    const fetchToutesLesLignes = async () => {
        const res = await axiosInstance.get(`/api/bordereau/page`, {
            params: { ...filtresParams(), page: 0, size: 100000 },
        });
        return res.data?.content || [];
    };

    // Liste des apporteurs présents dans les bordereaux (pour le filtre de l'état).
    // Basée sur le nom d'apporteur RÉSOLU (agence rattachée OU repli sur l'observation) —
    // même résolution que la colonne « Apporteur » du tableau, sinon les bordereaux à agence
    // nulle/historisée seraient absents du menu (filtre incomplet).
    // Caissiers présents dans les bordereaux versés (dédupliqués par NOM : un même agent peut
    // avoir plusieurs comptes historisés, cf. m_utilisateur dont le code_id change entre versions).
    const caissierOptions = useMemo(
        () => (optionsServer?.caissiers || []).map((n) => ({ value: n, label: n })),
        [optionsServer]);

    const apporteurOptions = useMemo(
        () => (optionsServer?.apporteurs || []).map((n) => ({ value: n, label: n })),
        [optionsServer]);

    const etatPeriodeLabel = (etatPeriode && etatPeriode[0] && etatPeriode[1])
        ? `PÉRIODE DU ${etatPeriode[0].format("DD/MM/YYYY")} AU ${etatPeriode[1].format("DD/MM/YYYY")}`
        : "";

    // Génère l'état des bordereaux VERSÉS (endpoint dédié) : période obligatoire, agence optionnelle.
    // Sans agence → l'endpoint renvoie toutes les agences, triées ; le doc groupe par agence (1 page/agence).
    const genererEtat = () => {
        if (!etatPeriode || !etatPeriode[0] || !etatPeriode[1]) {
            toast.warning("Veuillez sélectionner une période.");
            return;
        }
        setEtatLoading(true);
        const params = {
            start: etatPeriode[0].format("YYYY-MM-DD"),
            end: etatPeriode[1].format("YYYY-MM-DD"),
        };
        // Filtre apporteur appliqué CÔTÉ CLIENT sur le nom résolu (agence OU observation) :
        // couvre les bordereaux à agence nulle/historisée que le filtre backend par code agence
        // laissait passer à travers les mailles.
        axiosInstance.get(`/api/bordereau/etat-versements`, { params })
            .then(async (res) => {
                const all = res.data || [];
                const data = etatAgence
                    ? all.filter((b) => beneficiaireNomOf(b) === etatAgence)
                    : all;
                setEtatData(data);
                // Logos des compagnies destinataires (une page/agence peut relever d'une compagnie
                // différente). On libère les anciennes URLs avant d'en recréer.
                setEtatLogos((prev) => { Object.values(prev).forEach((u) => { try { URL.revokeObjectURL(u); } catch (e) { /* noop */ } }); return {}; });
                const codes = Array.from(new Set(data.map(organismeCodeOf).filter(Boolean)));
                const logos = {};
                await Promise.all(codes.map((code) =>
                    getCompagnieLogo(code)
                        .then(async (response) => {
                            const bytes = await response.arrayBuffer();
                            logos[code] = URL.createObjectURL(new Blob([bytes], { type: "image/png" }));
                        })
                        .catch(() => { /* logo indisponible → en-tête Pool seul */ })
                ));
                setEtatLogos(logos);
                setShowEtatModal(true);
            })
            .catch(() => toast.error("Erreur lors de la génération de l'état."))
            .finally(() => setEtatLoading(false));
    };

    // Recharge la liste à chaque changement de période (et au montage).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Tout changement de filtre ou de page repart au serveur. La recherche est débouncée
    // pour ne pas déclencher un appel à chaque frappe.
    useEffect(() => {
        const id = setTimeout(() => fetchData(), search ? 400 : 0);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periode, page, pageSize, search, statutFilter, caissierFilter, etatAgence]);

    // Retour en page 1 dès qu'un filtre change : rester sur la page courante afficherait
    // une page vide sur un périmètre réduit.
    useEffect(() => {
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periode, search, statutFilter, caissierFilter, etatAgence]);

    // Options des filtres (apporteurs / caissiers) sur tout le périmètre de la période.
    useEffect(() => {
        const params = {};
        if (periode && periode[0] && periode[1]) {
            params.startDate = periode[0].format("YYYY-MM-DD");
            params.endDate = periode[1].format("YYYY-MM-DD");
        }
        axiosInstance.get(`/api/bordereau/page/options`, { params })
            .then((res) => setOptionsServer(res.data || { apporteurs: [], caissiers: [] }))
            .catch(() => { /* filtres vides plutôt qu'un écran en erreur */ });
    }, [periode]);

    useEffect(() => {
        axiosInstance.get(`/api/parametre/type/BANQUE`)
            .then((res) => setBanques(res.data || []))
            .catch((err) => console.log(err));
        // Pool : on charge les compagnies pour afficher leur raison sociale dans le tag.
        if (isPool) {
            axiosInstance.get(`/api/organisme/assurance`)
                .then((res) => {
                    const map = {};
                    (res.data || []).forEach((o) => { if (o?.code) map[o.code] = o.raisonSociale || o.code; });
                    setCompagnies(map);
                })
                .catch((err) => console.log(err));
        }
    }, []);

    const compagnieLabelOf = (b) => {
        const code = organismeCodeOf(b);
        if (!code) return null;
        return compagnies[code] || code;
    };

    const [etatJournalierLoading, setEtatJournalierLoading] = useState(false);
    // Séparateur de milliers : NE PAS utiliser toLocaleString("fr-FR") — il produit une espace
    // insécable étroite (U+202F) que jsPDF ne sait pas rendre et affiche « / » (« 286/165 »).
    // Pattern éprouvé du projet : format en-DE (points séparateurs) puis espace ASCII.
    const pdfMontant = (v) => new Intl.NumberFormat("en-DE").format(Math.trunc(Number(v) || 0)).replace(/\./g, " ");

    /**
     * ÉTAT JOURNALIER des versements en compagnie, sur DEUX niveaux :
     * CAISSIER (= validateur du versement) → AGENCE apporteuse, avec un sous-total par
     * agence, un sous-total par caissier et le total général.
     * Ne retient que les bordereaux VERSÉS : un état de caisse ne porte que sur l'argent remis.
     */
    const genererEtatJournalierCaissier = async () => {
        setEtatJournalierLoading(true);
        try {
            // Toutes les lignes du périmètre filtré : `filteredDatas` ne contient plus que la
            // PAGE affichée depuis le passage en pagination serveur → l'état aurait été tronqué.
            const toutes = await fetchToutesLesLignes();
            const lignes = (toutes || []).filter((b) => isVerse(b));
            if (!lignes.length) {
                toast.info("Aucun bordereau versé sur la période/les filtres sélectionnés.");
                return;
            }

            // Regroupement à deux niveaux : caissier → agence apporteuse.
            // Map imbriquée (caissier → Map(agence → lignes)), tri alphabétique aux deux
            // niveaux pour un rendu stable d'une génération à l'autre.
            const groupes = new Map();
            lignes.forEach((b) => {
                const ca = caissierNomOf(b) || "Caissier non renseigné";
                const ag = beneficiaireNomOf(b) || "Agence non renseignée";
                if (!groupes.has(ca)) groupes.set(ca, new Map());
                const parAgence = groupes.get(ca);
                if (!parAgence.has(ag)) parAgence.set(ag, []);
                parAgence.get(ag).push(b);
            });

            const doc = new jsPDF({ orientation: "portrait" });
            let enteteData = null, logos = [], logoCompagnie = null;
            // Logo de la compagnie de l'utilisateur : entête à 2 logos (compagnie à gauche,
            // Pool à droite), comme les Conditions Particulières. null pour un user du Pool.
            try { [enteteData, logos, logoCompagnie] = await Promise.all(
                [loadEntete(), loadLogos(), loadLogoCompagnieConnectee()]); } catch (e) { /* optionnels */ }
            const formattedNow = new Date().toLocaleString("fr-FR", {
                day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
            });

            const body = [];
            let totalGeneral = 0, nbGeneral = 0;
            [...groupes.keys()].sort().forEach((ca) => {
                const parAgence = groupes.get(ca);
                let totalCaissier = 0, nbCaissier = 0;

                body.push([{ content: `Caissier : ${ca}`, colSpan: 5,
                    styles: { fillColor: NAVY, textColor: 255, fontStyle: "bold" } }]);

                [...parAgence.keys()].sort().forEach((ag) => {
                    const rows = parAgence.get(ag);
                    const totalAgence = rows.reduce((sum, b) => sum + (Number(b.montantTotal) || 0), 0);

                    // Bandeau d'agence : plus clair que le bandeau caissier pour que la
                    // hiérarchie des deux niveaux reste lisible en noir et blanc.
                    body.push([{ content: `Agence : ${ag}`, colSpan: 5,
                        styles: { fillColor: [219, 223, 240], textColor: 20, fontStyle: "bold" } }]);

                    rows.forEach((b) => body.push([
                        b.dateReglement ? dayjs(b.dateReglement).format("DD/MM/YYYY") : "-",
                        b.numeroBordereau || "-",
                        beneficiaireNomOf(b) || "-",
                        b.modePaiement || "-",
                        { content: pdfMontant(b.montantTotal), styles: { halign: "right" } },
                    ]));

                    body.push([
                        { content: `Sous-total ${ag} (${rows.length} bordereau(x))`, colSpan: 4,
                            styles: { halign: "right", fontStyle: "bold", fillColor: [240, 242, 250] } },
                        { content: pdfMontant(totalAgence),
                            styles: { halign: "right", fontStyle: "bold", fillColor: [240, 242, 250] } },
                    ]);
                    totalCaissier += totalAgence; nbCaissier += rows.length;
                });

                body.push([
                    { content: `Total ${ca} (${nbCaissier} bordereau(x))`, colSpan: 4,
                        styles: { halign: "right", fontStyle: "bold", fillColor: [232, 234, 246] } },
                    { content: pdfMontant(totalCaissier),
                        styles: { halign: "right", fontStyle: "bold", fillColor: [232, 234, 246] } },
                ]);
                totalGeneral += totalCaissier; nbGeneral += nbCaissier;
            });

            autoTable(doc, {
                head: [["Date versement", "N° bordereau", "Point de vente", "Mode", "Montant"]],
                body,
                foot: [[
                    { content: `TOTAL GÉNÉRAL (${nbGeneral} bordereau(x))`, colSpan: 4,
                        styles: { halign: "right", fontStyle: "bold" } },
                    { content: pdfMontant(totalGeneral), styles: { halign: "right", fontStyle: "bold" } },
                ]],
                theme: "grid",
                styles: { fontSize: 8, cellPadding: 1.6, overflow: "linebreak", valign: "middle" },
                headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8, fontStyle: "bold" },
                footStyles: { fillColor: [243, 244, 246], textColor: 20 },
                margin: { top: 32, bottom: 22, left: 14, right: 14 },
                columnStyles: { 0: { cellWidth: 28, halign: "center" }, 4: { cellWidth: 32, halign: "right" } },
                didDrawPage: () => drawEtatHeaderFooter(doc, {
                    title: "ÉTAT JOURNALIER DES VERSEMENTS EN COMPAGNIE",
                    subtitle: `Par caissier et par agence · ${periode?.[0] && periode?.[1]
                        ? `du ${periode[0].format("DD/MM/YYYY")} au ${periode[1].format("DD/MM/YYYY")}`
                        : "toutes périodes"}${caissierFilter ? ` · ${caissierFilter}` : ""}`,
                    enteteData, logos, formattedNow, logoCompagnie,
                }),
            });
            finalizeEtatPdf(doc);
            const jour = periode?.[0] ? periode[0].format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
            doc.save(`etat_journalier_versements_${jour}.pdf`);
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors de la génération de l'état journalier.");
        } finally {
            setEtatJournalierLoading(false);
        }
    };

    // Filtrage, tri (date d'édition décroissante) et pagination sont faits EN BASE
    // (/api/bordereau/page) : la page reçue est déjà la bonne, on l'affiche telle quelle.
    const filteredDatas = allDatas;

    // ── Indicateurs ──
    // Agrégés côté SERVEUR sur tout le périmètre filtré : les calculer sur `filteredDatas`
    // ne porterait plus que sur la page affichée (10 lignes) → KPI faux.
    const stats = useMemo(() => ({
        montantTotal: statsServer?.montantTotal || 0,
        nbVerses: statsServer?.nbVerses || 0,
        nbNonVerses: statsServer?.nbNonVerses || 0,
    }), [statsServer]);

    // Le détail des règlements n'est PLUS transporté par la liste (DTO allégé) : on le charge
    // À LA DEMANDE à l'ouverture. `nbReglements` évite un appel inutile pour un bordereau vide.
    // codeId du bordereau en cours de chargement : permet de ne faire tourner QUE son
    // bouton (un booléen global les mettrait tous en attente).
    const [apercuLoadingId, setApercuLoadingId] = useState(null);
    const apercu = async (bord) => {
        if ((bord?.nbReglements ?? (bord?.reglements || []).length) === 0) {
            toast.warn("Ce bordereau ne contient aucun règlement");
            return;
        }
        setApercuLoadingId(bord.codeId);
        try {
            // On NE peut PAS utiliser /api/bordereau/{id} : il renvoie l'ENTITÉ, dont
            // `Reglement.contrat` est annoté @JsonBackReference → le contrat n'est pas sérialisé
            // (ni police, ni souscripteur, ni dates, ni catégorie). Le document a besoin du DTO.
            // On rappelle donc /api/bordereau (qui renvoie des BordereauDTO complets) en le
            // BORNANT au jour d'édition du bordereau : la fenêtre est minuscule, donc pas de
            // retour à un chargement massif, et la forme reçue est exactement celle d'avant.
            const jour = bord?.createdAt ? dayjs(bord.createdAt).format("YYYY-MM-DD") : null;
            const res = await axiosInstance.get(`/api/bordereau`, {
                params: jour ? { startDate: jour, endDate: jour } : {},
            });
            const complet = (res.data || []).find((x) => x.codeId === bord.codeId);
            if (!complet) {
                toast.error("Détail du bordereau introuvable.");
                return;
            }
            setBordereauToPay(complet);
            setOpenModalBorderau(true);
        } catch (e) {
            console.log(e);
            toast.error("Impossible de charger le détail de ce bordereau.");
        } finally {
            setApercuLoadingId(null);
        }
    };

    const encaisserBordereau = (bord) => {
        setEditMode(false);
        setBordereauToPay(bord);
        setModePaiement("");
        setNumeroPaiement("P");
        setBanque("BANQUE");
        setDateVirement(null);
        setCommentaireEncaissement("");
        modePaiementForm.resetFields();
        setModalModePaiement(true);
    };

    // Correction du mode de règlement d'un bordereau déjà versé : on pré-remplit
    // la modale avec les valeurs enregistrées.
    const modifierModePaiement = (bord) => {
        setEditMode(true);
        setBordereauToPay(bord);
        setModePaiement(bord.modePaiement || "");
        setNumeroPaiement(bord.numeroPaiement || "");
        setBanque(bord.banque || "BANQUE");
        const dv = bord.modePaiement === "VIREMENT" && bord.dateReglement ? dayjs(bord.dateReglement) : null;
        setDateVirement(dv);
        modePaiementForm.setFieldsValue({
            modePaiement: bord.modePaiement || undefined,
            numeroPaiement: bord.numeroPaiement || undefined,
            banque: bord.banque || undefined,
            dateVirement: dv || undefined,
        });
        setModalModePaiement(true);
    };

    const alertReglementBordereau = (bd) => {
        if (!modePaiement) { toast.warn("Sélectionnez un mode de paiement"); return; }
        confirm({
            title: "Êtes-vous sûr de vouloir régler ce bordereau ?",
            icon: <ExclamationCircleFilled />,
            content: "Toutes vos actions sont historisées !",
            okText: "Oui, régler", okType: "danger", cancelText: "Annuler",
            onOk() { reglementBordereau(bd); },
        });
    };

    const alertModifierModePaiement = (bd) => {
        if (!modePaiement) { toast.warn("Sélectionnez un mode de paiement"); return; }
        confirm({
            title: "Modifier le mode de règlement de ce bordereau ?",
            icon: <ExclamationCircleFilled />,
            content: "Cette correction est historisée.",
            okText: "Oui, modifier", okType: "danger", cancelText: "Annuler",
            onOk() { enregistrerModePaiement(bd); },
        });
    };

    // Champs cohérents selon le mode : pas de n° / banque pour les espèces.
    // VIREMENT : banque + référence de paiement (portée par numeroPaiement) + date de valeur,
    // cette dernière devenant la date de règlement du bordereau côté backend.
    const payloadModePaiement = (bd) => ({
        codeId: bd.codeId,
        modePaiement,
        numeroPaiement: modePaiement === "ESPECES" ? null : numeroPaiement,
        banque: (modePaiement === "BANQUE" || modePaiement === "VIREMENT") ? banque : null,
        dateVirement: modePaiement === "VIREMENT" && dateVirement
            ? dayjs(dateVirement).toISOString()
            : null,
        commentaireEncaissement: commentaireEncaissement && commentaireEncaissement.trim()
            ? commentaireEncaissement.trim() : undefined,
    });

    const reglementBordereau = (bd) => {
        axiosInstance.put(`/api/bordereau/encaisser`, payloadModePaiement(bd))
            .then(() => {
                toast.success("Règlement effectué avec succès !");
                setModalModePaiement(false);
                fetchData();
            })
            .catch((err) => { console.log(err); toast.error("Erreur lors du règlement"); });
    };

    const enregistrerModePaiement = (bd) => {
        axiosInstance.put(`/api/bordereau/mode-paiement`, payloadModePaiement(bd))
            .then(() => {
                toast.success("Mode de règlement modifié avec succès !");
                setModalModePaiement(false);
                fetchData();
            })
            .catch((err) => { console.log(err); toast.error("Erreur lors de la modification"); });
    };

    // ── Annulation d'un bordereau non versé (HLA101) ──────────────────────
    const ouvrirAnnulation = (bd) => {
        setBordereauAAnnuler(bd);
        setMotifAnnulation("");
        setModalAnnulation(true);
    };

    const confirmerAnnulation = () => {
        if (!motifAnnulation.trim()) {
            toast.error("Le motif d'annulation est obligatoire.");
            return;
        }
        setAnnulationEnCours(true);
        axiosInstance
            .put(`/api/bordereau/annuler/${bordereauAAnnuler?.codeId}`, { motif: motifAnnulation.trim() })
            .then(() => {
                toast.success("Bordereau annulé. Les règlements liés redeviennent à reverser.");
                setModalAnnulation(false);
                setBordereauAAnnuler(null);
                fetchData();
            })
            .catch((err) => {
                // Le backend renvoie un message explicite (déjà versé, droit manquant, motif vide).
                const msg = err?.response?.data?.message || err?.response?.data?.error
                    || "Erreur lors de l'annulation du bordereau";
                toast.error(msg);
            })
            .finally(() => setAnnulationEnCours(false));
    };

    // ── Modification EXCEPTIONNELLE de la période affichée (HLA101) ────────
    const ouvrirPeriode = (bd) => {
        setBordereauPeriode(bd);
        setPeriodeEdit([
            bd?.periodeDebut ? dayjs(bd.periodeDebut) : null,
            bd?.periodeFin ? dayjs(bd.periodeFin) : null,
        ]);
        setModalPeriode(true);
    };

    const confirmerPeriode = () => {
        setPeriodeEnCours(true);
        axiosInstance
            .put(`/api/bordereau/${bordereauPeriode?.codeId}/periode`, null, {
                params: {
                    startDate: periodeEdit && periodeEdit[0] ? periodeEdit[0].format("YYYY-MM-DD") : null,
                    endDate: periodeEdit && periodeEdit[1] ? periodeEdit[1].format("YYYY-MM-DD") : null,
                },
            })
            .then(() => {
                toast.success("Période du bordereau modifiée.");
                setModalPeriode(false);
                setBordereauPeriode(null);
                fetchData();
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || err?.response?.data?.error
                    || "Erreur lors de la modification de la période";
                toast.error(msg);
            })
            .finally(() => setPeriodeEnCours(false));
    };

    const columns = [
        { title: "N° bordereau", dataIndex: "numeroBordereau", key: "numeroBordereau", render: (v) => <Text strong>{v}</Text> },
        // Colonne Compagnie : uniquement pour le Pool (qui voit toutes les compagnies).
        ...(isPool ? [{
            title: "Compagnie", key: "compagnie",
            render: (_, r) => {
                const label = compagnieLabelOf(r);
                return label ? <Tag color="purple" style={{ borderRadius: 6 }}><BankOutlined /> {label}</Tag> : <Tag>—</Tag>;
            },
        }] : []),
        {
            title: "Point de vente", dataIndex: "agence", key: "agence",
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <Text strong style={{ fontSize: 13 }}>{beneficiaireNomOf(r) || "—"}</Text>
                    {r.agence?.codeAgence ? <Tag color="cyan" style={{ borderRadius: 6, fontSize: 11 }}><BankOutlined /> {r.agence.codeAgence}</Tag> : null}
                </Space>
            ),
        },
        {
            title: "Montant", dataIndex: "montantTotal", key: "montantTotal",
            align: "right", render: (_, r) => <Text strong>{displayNumbers(r.montantTotal)}</Text>,
        },
        {
            title: "Statut", dataIndex: "modePaiement", key: "modePaiement",
            render: (_, r) => (
                <div>
                    {isVerse(r)
                        ? <Tag color={MODE_COLORS[r.modePaiement] || "green"}>Versé · {r.modePaiement}</Tag>
                        : <Tag color="default">Non versé</Tag>}
                    {r.commentaireEncaissement ? (
                        <div title={r.commentaireEncaissement}
                            style={{ fontSize: 11, color: "#888", fontStyle: "italic", marginTop: 2, maxWidth: 180,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            💬 {r.commentaireEncaissement}
                        </div>
                    ) : null}
                </div>
            ),
        },
        {
            title: "Caissier", dataIndex: "caissier", key: "caissier",
            // Validateur du versement ; vide tant que le bordereau n'est pas versé.
            render: (_, r) => (caissierNomOf(r)
                ? <Text style={{ fontSize: 12 }}>{caissierNomOf(r)}</Text>
                : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>),
        },
        {
            title: "Date édition", dataIndex: "createdAt", key: "createdAt",
            render: (_, r) => (r.createdAt ? frenchDate(r.createdAt) : "-"),
            // Pas de `sorter` client : la liste étant paginée côté serveur (ORDER BY
            // b.created_at DESC), un tri local ne réordonnerait que les lignes de la page
            // affichée — l'utilisateur croirait trier l'ensemble.
        },
        { title: "Date versement", dataIndex: "dateReglement", key: "dateReglement", render: (_, r) => (isVerse(r) && r.dateReglement ? frenchDate(r.dateReglement) : "-") },
        {
            title: "", key: "actions", width: 110,
            render: (_, r) => (
                <Space>
                    <Tooltip title="Aperçu / Imprimer">
                        <Tooltip title={apercuLoadingId === r.codeId
                            ? "Chargement du détail du bordereau…"
                            : "Aperçu / impression"}>
                            <Button
                                size="small"
                                shape="circle"
                                icon={<PrinterOutlined />}
                                onClick={() => apercu(r)}
                                // Le détail (règlements + contrats) est chargé à la demande : on
                                // annonce l'attente plutôt que de laisser un clic sans retour.
                                loading={apercuLoadingId === r.codeId}
                                disabled={!!apercuLoadingId}
                            />
                        </Tooltip>
                    </Tooltip>
                    {!isVerse(r) && LOGGED_USER?.agence?.typeAgence === "SIEGE" ? (
                        <Tooltip title="Encaisser le bordereau">
                            <Button size="small" shape="circle" icon={<CreditCardOutlined />}
                                style={{ background: COLORS.warning, color: "#fff", borderColor: COLORS.warning }}
                                onClick={() => encaisserBordereau(r)} />
                        </Tooltip>
                    ) : null}
                    {isVerse(r) && LOGGED_USER?.agence?.typeAgence === "SIEGE" ? (
                        <Tooltip title="Modifier le mode de règlement">
                            <Button size="small" shape="circle" icon={<EditOutlined />}
                                onClick={() => modifierModePaiement(r)} />
                        </Tooltip>
                    ) : null}
                    {/* Annulation (HLA101) : uniquement tant que le bordereau n'est PAS versé.
                        Le backend rejoue les deux gardes (droit + non versé) : l'UI ne protège rien. */}
                    {peutAnnuler && !isVerse(r) ? (
                        <Tooltip title="Annuler le bordereau et libérer ses règlements">
                            <Button size="small" shape="circle" danger icon={<StopOutlined />}
                                onClick={() => ouvrirAnnulation(r)} />
                        </Tooltip>
                    ) : null}
                    {/* Modification exceptionnelle de la période affichée (HLA101, comme l'annulation). */}
                    {peutAnnuler ? (
                        <Tooltip title="Modifier la période affichée sur le bordereau">
                            <Button size="small" shape="circle" icon={<CalendarOutlined />}
                                onClick={() => ouvrirPeriode(r)} />
                        </Tooltip>
                    ) : null}
                </Space>
            ),
        },
    ];

    return (
        <div class="layout-wrapper layout-content-navbar">
            <div class="layout-container">
                <SideBar currentMenu={"CAISSE"} />
                <div class="layout-page">
                    <NavBar />
                    <div class="content-wrapper">
                        <div class="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{
                                background: COLORS.gradient, borderRadius: 16, padding: "22px 26px",
                                marginBottom: 18, color: "#fff",
                                display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12,
                            }}>
                                <div>
                                    <Title level={3} style={{ color: "#fff", margin: 0 }}>Bordereaux de versement en compagnie</Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                        Consultez, imprimez et encaissez les bordereaux de reversement générés
                                    </Text>
                                </div>
                                <Space wrap>
                                    <Tooltip title="Rafraîchir">
                                        <Button icon={<ReloadOutlined />} onClick={fetchData} />
                                    </Tooltip>
                                </Space>
                            </div>

                            {/* Indicateurs */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
                                <Col xs={12} md={6}>
                                    <StatCard title="Bordereaux" subtitle="filtrés"
                                        value={`${total}`} icon="bx bx-file"
                                        iconBg={COLORS.primary} gradient={COLORS.primary} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Montant total" subtitle="filtré"
                                        value={`${displayNumbers(Math.trunc(stats.montantTotal))} F`} icon="bx bx-wallet"
                                        iconBg={COLORS.success} gradient={COLORS.success} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Versés" subtitle="déjà encaissés"
                                        value={`${stats.nbVerses}`} icon="bx bx-check-circle"
                                        iconBg={COLORS.secondary} gradient={COLORS.secondary} />
                                </Col>
                                <Col xs={12} md={6}>
                                    <StatCard title="Non versés" subtitle="en attente"
                                        value={`${stats.nbNonVerses}`} icon="bx bx-time-five"
                                        iconBg={COLORS.warning} gradient={COLORS.warning} />
                                </Col>
                            </Row>

                            {/* Filtres */}
                            <Card bordered={false} style={{ borderRadius: 14, marginBottom: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 16 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={9}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Recherche (point de vente / n° bordereau)</Text>
                                        <Input.Search allowClear placeholder="Nom du point de vente ou numéro de bordereau"
                                            value={search} onChange={(e) => setSearch(e.target.value)} />
                                    </Col>
                                    <Col xs={24} md={7}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Période (création)</Text>
                                        <RangePicker style={{ width: "100%" }} format="DD/MM/YYYY"
                                            value={periode} onChange={setPeriode} allowClear={false} />
                                    </Col>
                                    <Col xs={24} md={5}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Statut</Text>
                                        <Select style={{ width: "100%" }} value={statutFilter} onChange={setStatutFilter}
                                            options={[
                                                { label: "Tous", value: "TOUS" },
                                                { label: "Versés", value: "VERSE" },
                                                { label: "Non versés", value: "NON_VERSE" },
                                            ]} />
                                    </Col>
                                    {/* Caissier = validateur du versement (celui qui a encaissé le bordereau). */}
                                    <Col xs={24} md={6}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Caissier</Text>
                                        <Select style={{ width: "100%" }} allowClear showSearch optionFilterProp="label"
                                            placeholder="Tous les caissiers"
                                            value={caissierFilter} onChange={(v) => setCaissierFilter(v || null)}
                                            options={caissierOptions} />
                                    </Col>
                                    <Col xs={24} md={6} style={{ display: "flex", alignItems: "flex-end" }}>
                                        <Tooltip title="Bordereaux versés groupés par caissier, avec sous-totaux et total général">
                                            <Button
                                                icon={<FilePdfOutlined />}
                                                onClick={genererEtatJournalierCaissier}
                                                loading={etatJournalierLoading}
                                                style={{
                                                    width: "100%", borderRadius: 8,
                                                    background: "#242166", borderColor: "#242166",
                                                    color: "#fff", fontWeight: 500,
                                                }}
                                            >
                                                État journalier
                                            </Button>
                                        </Tooltip>
                                    </Col>
                                </Row>
                            </Card>

                            {/* État des reversements versés (période + agence optionnelle) */}
                            <Card bordered={false} style={{ borderRadius: 14, marginBottom: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 16 }}>
                                <Row gutter={[12, 12]} align="bottom">
                                    <Col xs={24} md={10}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Période (date de versement)</Text>
                                        <RangePicker style={{ width: "100%" }} format="DD/MM/YYYY"
                                            value={etatPeriode} onChange={setEtatPeriode} allowClear={false} />
                                    </Col>
                                    <Col xs={24} md={10}>
                                        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Point de vente (optionnel)</Text>
                                        <Select style={{ width: "100%" }} allowClear showSearch optionFilterProp="label"
                                            placeholder="Tous les points de vente (une page par point de vente)"
                                            value={etatAgence} onChange={setEtatAgence} options={apporteurOptions} />
                                    </Col>
                                    <Col xs={24} md={4}>
                                        <Button type="primary" icon={<FileDoneOutlined />} block
                                            loading={etatLoading} onClick={genererEtat}>
                                            Éditer l'état
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Tableau */}
                            <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                                bodyStyle={{ padding: 8 }}>
                                <ConfigProvider locale={frFR}>
                                    <Spin spinning={loadingData}>
                                        <Table
                                            columns={columns}
                                            dataSource={filteredDatas}
                                            rowKey="codeId"
                                            size="middle"
                                            locale={{ emptyText: <Empty description="Aucun bordereau de reversement" /> }}
                                            pagination={{
                                                current: page,
                                                pageSize,
                                                total,
                                                showSizeChanger: true,
                                                showTotal: (t) => `${t} bordereau(x)`,
                                                onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                                            }}
                                        />
                                    </Spin>
                                </ConfigProvider>
                            </Card>
                        </div>
                        <Footer />
                        <div class="content-backdrop fade"></div>
                    </div>
                </div>
            </div>

            {/* Modale aperçu / impression */}
            <Modal
                title={<Space><FileDoneOutlined style={{ color: COLORS.primary }} />Bordereau de reversement N° {bordereauToPay?.numeroBordereau}</Space>}
                centered
                open={openModalBorderau}
                onCancel={() => setOpenModalBorderau(false)}
                width={1250}
                zIndex={2000}
                footer={[<Button key="close" type="primary" icon={<CheckCircleTwoTone />} onClick={() => setOpenModalBorderau(false)}>Fermer</Button>]}
            >
                <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    {bordereauToPay ? <PrintableBordereau bordereau={bordereauToPay} /> : null}
                </div>
            </Modal>

            {/* Modale aperçu / impression de l'ÉTAT des reversements versés (groupé par agence) */}
            <Modal
                title={<Space><FileDoneOutlined style={{ color: COLORS.primary }} />État des bordereaux de reversement versés</Space>}
                centered
                open={showEtatModal}
                onCancel={() => setShowEtatModal(false)}
                width={1250}
                zIndex={2000}
                footer={[
                    <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrintEtat}>Imprimer</Button>,
                    <Button key="close" onClick={() => setShowEtatModal(false)}>Fermer</Button>,
                ]}
            >
                <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <EtatBordereauxReversement ref={etatPrintRef} bordereaux={etatData} periode={etatPeriodeLabel} logos={etatLogos} />
                </div>
            </Modal>

            {/* Modale d'encaissement / correction du mode de règlement */}
            <Modal
                title={editMode
                    ? `Modifier le mode de règlement — Bordereau N° ${bordereauToPay?.numeroBordereau || ""}`
                    : "Encaisser le bordereau — mode de paiement"}
                centered
                open={modalModePaiement}
                onOk={() => (editMode
                    ? alertModifierModePaiement(bordereauToPay)
                    : alertReglementBordereau(bordereauToPay))}
                onCancel={() => setModalModePaiement(false)}
                okText={editMode ? "Enregistrer la modification" : "Valider l'encaissement"}
            >
                <Form form={modePaiementForm} layout="vertical" name="modePaiementForm">
                    <Form.Item name="modePaiement" label="Mode de paiement" rules={[{ required: true }]}>
                        <Select onChange={(value) => setModePaiement(value)} placeholder="Sélectionnez le mode de paiement">
                            <Option value="ESPECES">ESPECES</Option>
                            <Option value="BANQUE">BANQUE (Chèque)</Option>
                            <Option value="VIREMENT">Banque (Virement)</Option>
                            <Option value="MOBILE_MONEY">TMONEY / FLOOZ</Option>
                        </Select>
                    </Form.Item>
                    {modePaiement === "BANQUE" || modePaiement === "MOBILE_MONEY" || modePaiement === "VIREMENT" ? (
                        <Form.Item name="numeroPaiement"
                            label={modePaiement === "BANQUE"
                                ? "Numéro de chèque"
                                : modePaiement === "VIREMENT"
                                    ? "Référence du paiement"
                                    : "Numéro FLOOZ / TMONEY"}
                            rules={[{ required: true, message: "Veuillez renseigner le champ !" }]}>
                            <Input
                                placeholder={modePaiement === "BANQUE"
                                    ? "Le numéro de chèque"
                                    : modePaiement === "VIREMENT"
                                        ? "Numéro de référence du virement"
                                        : "Le numéro de téléphone MOOV (FLOOZ) / TOGOCOM (TMONEY)"}
                                onChange={(e) => setNumeroPaiement(e.target.value)} />
                        </Form.Item>
                    ) : null}
                    {modePaiement === "BANQUE" || modePaiement === "VIREMENT" ? (
                        <Form.Item name="banque" label="Banque" rules={[{ required: true }]}>
                            <Select onChange={(value) => setBanque(value)} placeholder="Sélectionnez la banque">
                                {banques.map((b) => (
                                    <Option key={b.parametreId} value={b.libelleParametre}>{b.libelleParametre}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    ) : null}
                    {modePaiement === "VIREMENT" ? (
                        <Form.Item name="dateVirement" label="Date du virement"
                            rules={[{ required: true, message: "Veuillez renseigner la date du virement !" }]}>
                            <DatePicker
                                style={{ width: "100%" }}
                                format="DD/MM/YYYY"
                                placeholder="Date du virement"
                                onChange={(d) => setDateVirement(d)} />
                        </Form.Item>
                    ) : null}
                    {!editMode && (
                        <Form.Item label="Observation / commentaire (facultatif)">
                            <Input.TextArea
                                rows={2}
                                value={commentaireEncaissement}
                                onChange={(e) => setCommentaireEncaissement(e.target.value)}
                                placeholder="Commentaire libre sur cet encaissement" />
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            {/* Annulation d'un bordereau non versé (HLA101) : motif OBLIGATOIRE. */}
            <Modal
                title={<span><StopOutlined style={{ color: COLORS.danger || "#ff4d4f", marginRight: 8 }} />
                    Annuler le bordereau {bordereauAAnnuler?.numeroBordereau}</span>}
                open={modalAnnulation}
                onCancel={() => setModalAnnulation(false)}
                okText="Confirmer l'annulation"
                cancelText="Renoncer"
                okButtonProps={{ danger: true, loading: annulationEnCours, disabled: !motifAnnulation.trim() }}
                onOk={confirmerAnnulation}
                destroyOnClose
            >
                <Typography.Paragraph>
                    Le bordereau sera annulé et <b>ses règlements redeviendront « à reverser »</b>.
                    Son numéro reste réservé et ne sera pas réattribué.
                </Typography.Paragraph>
                <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
                    Bénéficiaire : <b>{beneficiaireNomOf(bordereauAAnnuler)}</b> · Montant :{" "}
                    <b>{displayNumbers(bordereauAAnnuler?.montantTotal)}</b>
                </Typography.Paragraph>
                <Form layout="vertical">
                    <Form.Item label={<b>Motif d'annulation</b>} required
                        help="Obligatoire : il est conservé avec le bordereau pour l'audit.">
                        <Input.TextArea
                            rows={3}
                            value={motifAnnulation}
                            onChange={(e) => setMotifAnnulation(e.target.value)}
                            placeholder="Ex : bordereau généré par erreur sur le mauvais point de vente"
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modification EXCEPTIONNELLE de la période affichée (HLA101). Purement documentaire :
                ne touche ni aux règlements ni aux montants. */}
            <Modal
                title={<span><CalendarOutlined style={{ marginRight: 8 }} />
                    Modifier la période — bordereau {bordereauPeriode?.numeroBordereau}</span>}
                open={modalPeriode}
                onCancel={() => setModalPeriode(false)}
                okText="Enregistrer la période"
                cancelText="Renoncer"
                okButtonProps={{ loading: periodeEnCours, disabled: !(periodeEdit && periodeEdit[0] && periodeEdit[1]) }}
                onOk={confirmerPeriode}
                destroyOnClose
            >
                <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
                    Cette période est <b>affichée telle quelle</b> sur le bordereau imprimé. La modifier
                    ne change ni les règlements liés ni les montants — opération réservée à l'habilitation
                    d'annulation (HLA101).
                </Typography.Paragraph>
                <Form layout="vertical">
                    <Form.Item label={<b>Période affichée (du … au …)</b>}>
                        <RangePicker
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            value={periodeEdit && periodeEdit[0] ? [periodeEdit[0], periodeEdit[1]] : null}
                            onChange={(v) => setPeriodeEdit(v && v[0] ? [v[0], v[1]] : [null, null])}
                            allowClear
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <div class="layout-overlay layout-menu-toggle"></div>
            <div class="drag-target"></div>
        </div>
    );
};

export default BorderauReversement;
